import { getDefaultStore } from 'jotai'

import * as logger from '@/utils/logger'

import type { RundownCueOrderItem } from '~shared/types'

import {
  groupRundownCues,
  reorderRundownCues,
} from '@/entities/rundown/api/requests'
import { rundownAtom, setCueOrderAtom } from '@/store/rundown.store'

import { flushCuesDerived } from './cue.derived'
import { CueDropzone, getCueDropzone, getTargetCueId } from './cue.dnd'
import { moveCueToGroup, reorderCues } from './cue.order'

import type { DragEndEvent } from '@dnd-kit/core'

const jotai = getDefaultStore()

/**
 * Send a new cue order, rolling back if the server rejects it.
 *
 * The order is applied before the request goes out, so the cue stays where the
 * user dropped it instead of snapping back for a round trip. Applied
 * synchronously, so the drag overlay animates into the new position rather than
 * back to the old one: see {@link flushCuesDerived}.
 */
async function applyCueOrder(
  rundownId: string,
  nextCueOrder: RundownCueOrderItem[],
  previousCueOrder: RundownCueOrderItem[],
) {
  jotai.set(setCueOrderAtom, nextCueOrder)
  flushCuesDerived()

  try {
    await reorderRundownCues({ rundownId, cues: nextCueOrder })
  } catch (error) {
    logger.error('[dropCue] reorder failed, restoring previous order', error)

    // guard: prevent calling `setCueOrderAtom` with bad state
    if (jotai.get(rundownAtom)?.id !== rundownId) return

    jotai.set(setCueOrderAtom, previousCueOrder)
    flushCuesDerived()
  }
}

/**
 * Apply a dropped cue.
 *
 * A plain function rather than a component callback: nothing here needs React,
 * so `CuesList` only has to hand over the dnd-kit event.
 *
 * @remarks
 *   Writes order through `rundownAtom`, which stays the only writer of cue
 *   order and mirrors into `rundownPageStore`, where the derived cue list reads
 *   it. Writing the valtio store directly would leave the two disagreeing until
 *   the next snapshot.
 */
export async function dropCue({ active, over }: DragEndEvent) {
  if (!over) return

  const rundown = jotai.get(rundownAtom)
  if (!rundown) return

  const sourceCueId = String(active.id)
  const dropzone = getCueDropzone(String(over.id))
  const targetCueId = getTargetCueId(String(over.id))

  if (!dropzone || !targetCueId) return

  // guard: don't allow drops onto self
  if (sourceCueId === targetCueId) return

  // `reorderCues` and `moveCueToGroup` leave this untouched, so it doubles as
  // the rollback value.
  const previousCueOrder = rundown.cues

  // Dropped onto a plain cue: the two become a new group. Server-side, because
  // it has to mint the group cue, so there is no order to apply up front.
  if (dropzone === CueDropzone.CUE) {
    try {
      await groupRundownCues({
        rundownId: rundown.id,
        beforeCueId: targetCueId,
        cuesToGroupIds: [targetCueId, sourceCueId],
      })
    } catch (error) {
      logger.error('[dropCue] grouping failed', error)
    }
    return
  }

  const nextCueOrder =
    dropzone === CueDropzone.GROUP
      ? // Dropped onto an existing group: the cue becomes its first child.
        moveCueToGroup(previousCueOrder, sourceCueId, targetCueId)
      : // Dropped between cues.
        reorderCues(
          previousCueOrder,
          sourceCueId,
          targetCueId,
          dropzone === CueDropzone.AFTER,
        )

  // Both operations hand back the order untouched when the move is impossible,
  // usually because the target was removed mid-drag. Nothing to send.
  if (nextCueOrder === previousCueOrder) {
    logger.warn('[dropCue] drop target is gone, order unchanged', {
      dropzone,
      sourceCueId,
      targetCueId,
    })
    return
  }

  await applyCueOrder(rundown.id, nextCueOrder, previousCueOrder)
}
